import type { RequestContext } from "../context/request-context.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import type { AIReviewRepository } from "../../repositories/ai-review-repository.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { PermissionCode as PermissionCodeValue } from "../../domain/identity/access-types.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import {
  AI_REVIEW_SUBJECT_LABELS,
  type AIAppliedChange,
  type AIReportReview,
  type AIReviewFinding,
  type AIReviewSubjectType
} from "../../domain/ai-review/ai-review-model.js";
import type { AIChatAnswer, AIProvider } from "../../domain/ai-review/ai-provider.js";
import { assertNoSecrets, normalizeAISettings, AI_PROVIDER_LABELS, type AISettings } from "../../domain/ai-review/ai-settings.js";
import { searchKnowledge, validateKnowledgeEntry, type KnowledgeEntry } from "../../domain/ai-review/technical-knowledge.js";
import { NO_KNOWLEDGE_SUPPORT, promptVersionOf } from "../../domain/ai-review/prompt-templates.js";
import type { AIReviewOrchestrator, AIReviewState } from "./ai-review-orchestrator.js";
import type { ReviewableItem } from "./ai-review-adapters.js";

const uuid = () => globalThis.crypto.randomUUID();
const SUBJECT_TYPES: readonly AIReviewSubjectType[] = ["ME", "LP", "PM", "USC", "BSCAN", "AVR", "RDO", "INTEGRITY"];

export interface AIReviewOverview {
  readonly awaitingReview: number;
  readonly reviewed: number;
  readonly languageCorrections: number;
  readonly technicalAlerts: number;
  readonly criticalInconsistencies: number;
  readonly unresolved: number;
  readonly provider: { id: string; label: string; available: boolean };
}

export interface AIPendingItem {
  readonly review: AIReportReview;
  readonly finding: AIReviewFinding;
}

/**
 * INSPECT IA REVIEW — FACHADA ÚNICA do /api/rpc (AIPlatformService).
 *
 * Toda chamada confere a permissão do ato; o orquestrador e o provedor
 * ficam por trás e nunca são expostos. Aplicar texto a um relatório exige a
 * permissão do INSPECT IA REVIEW E a de edição do próprio módulo — a
 * revisão não abre um caminho de escrita mais fraco que a tela do relatório.
 */
export class InspectIaReviewService {
  private readonly auth = new RbacAuthorizationService();

  constructor(
    private readonly orchestrator: AIReviewOrchestrator,
    private readonly repo: AIReviewRepository,
    private readonly localProvider: AIProvider,
    private readonly audit?: AuditRecorder
  ) {}

  private type(t: unknown): AIReviewSubjectType {
    if (!SUBJECT_TYPES.includes(t as AIReviewSubjectType)) throw new AppError(ErrorCode.Validation, "Invalid subject type", { safeMessage: "Tipo de documento inválido para a revisão IA." });
    return t as AIReviewSubjectType;
  }

  private canApplyLanguage(context: RequestContext, type: AIReviewSubjectType): boolean {
    return this.auth.can(context, PermissionCode.AiReviewApplyLanguage) && this.auth.can(context, this.orchestrator.adapterFor(type).editPermission);
  }

  private requireModuleRead(context: RequestContext, type: AIReviewSubjectType): void {
    this.auth.require(context, this.orchestrator.adapterFor(type).readPermission);
  }

  private requireWrite(context: RequestContext, type: AIReviewSubjectType, permission: PermissionCodeValue): void {
    this.auth.require(context, permission);
    this.auth.require(context, this.orchestrator.adapterFor(type).editPermission);
  }

  async supportedTypes(context: RequestContext): Promise<Array<{ type: AIReviewSubjectType; label: string }>> {
    this.auth.require(context, PermissionCode.AiReviewRead);
    return this.orchestrator.supportedTypes().map(type => ({ type, label: AI_REVIEW_SUBJECT_LABELS[type] }));
  }

  /** Documentos do tipo que a pessoa pode ler, pela listagem do próprio módulo. */
  async listReviewable(context: RequestContext, type: AIReviewSubjectType): Promise<ReviewableItem[]> {
    this.auth.require(context, PermissionCode.AiReviewRead);
    const t = this.type(type);
    this.requireModuleRead(context, t);
    return this.orchestrator.adapterFor(t).list(context);
  }

  async getReviewState(context: RequestContext, type: AIReviewSubjectType, reportId: string): Promise<AIReviewState> {
    this.auth.require(context, PermissionCode.AiReviewRead);
    const t = this.type(type);
    this.requireModuleRead(context, t);
    return this.orchestrator.state(context, t, reportId);
  }

  /** Inicia a revisão e devolve na hora (EM EXECUÇÃO); a tela acompanha por `getReviewState`. */
  async requestReview(context: RequestContext, type: AIReviewSubjectType, reportId: string): Promise<AIReportReview> {
    this.auth.require(context, PermissionCode.AiReviewExecute);
    const t = this.type(type);
    this.requireModuleRead(context, t);
    return this.orchestrator.requestReview(context, t, reportId, { canApplyLanguage: this.canApplyLanguage(context, t) });
  }

  /** Mesma revisão, esperando o fim. */
  async runReview(context: RequestContext, type: AIReviewSubjectType, reportId: string): Promise<AIReportReview> {
    this.auth.require(context, PermissionCode.AiReviewExecute);
    const t = this.type(type);
    this.requireModuleRead(context, t);
    return this.orchestrator.runReview(context, t, reportId, { canApplyLanguage: this.canApplyLanguage(context, t) });
  }

  async applySafeCorrections(context: RequestContext, type: AIReviewSubjectType, reportId: string): Promise<AIReportReview> {
    const t = this.type(type);
    this.requireWrite(context, t, PermissionCode.AiReviewApplyLanguage);
    return this.orchestrator.applySafe(context, t, reportId, { canApplyLanguage: true });
  }

  async acceptFinding(context: RequestContext, findingId: string): Promise<AIReportReview> {
    const finding = await this.findingOrFail(findingId);
    this.requireWrite(context, finding.subjectType, PermissionCode.AiReviewResolveFinding);
    return this.orchestrator.accept(context, findingId, { canApplyLanguage: this.canApplyLanguage(context, finding.subjectType) });
  }

  async applyManualEdit(context: RequestContext, findingId: string, text: string): Promise<AIReportReview> {
    const finding = await this.findingOrFail(findingId);
    this.requireWrite(context, finding.subjectType, PermissionCode.AiReviewResolveFinding);
    return this.orchestrator.manualEdit(context, findingId, text, { canApplyLanguage: this.canApplyLanguage(context, finding.subjectType) });
  }

  async rejectFinding(context: RequestContext, findingId: string, reason: string): Promise<AIReviewFinding> {
    const finding = await this.findingOrFail(findingId);
    this.auth.require(context, PermissionCode.AiReviewResolveFinding);
    this.requireModuleRead(context, finding.subjectType);
    return this.orchestrator.reject(context, findingId, reason);
  }

  async acknowledgeFinding(context: RequestContext, findingId: string): Promise<AIReviewFinding> {
    const finding = await this.findingOrFail(findingId);
    this.auth.require(context, PermissionCode.AiReviewResolveFinding);
    this.requireModuleRead(context, finding.subjectType);
    return this.orchestrator.acknowledge(context, findingId);
  }

  async justifyFinding(context: RequestContext, findingId: string, justification: string): Promise<AIReviewFinding> {
    const finding = await this.findingOrFail(findingId);
    this.requireWrite(context, finding.subjectType, PermissionCode.AiReviewResolveFinding);
    return this.orchestrator.justify(context, findingId, justification);
  }

  async listAppliedChanges(context: RequestContext, type: AIReviewSubjectType, reportId: string): Promise<AIAppliedChange[]> {
    this.auth.require(context, PermissionCode.AiReviewHistory);
    const t = this.type(type);
    this.requireModuleRead(context, t);
    return this.repo.listChanges({ subjectType: t, reportId });
  }

  async revertChange(context: RequestContext, type: AIReviewSubjectType, reportId: string, changeId: string): Promise<void> {
    const t = this.type(type);
    this.requireWrite(context, t, PermissionCode.AiReviewResolveFinding);
    await this.orchestrator.revertChange(context, changeId, t, reportId);
  }

  private async findingOrFail(id: string): Promise<AIReviewFinding> {
    const f = await this.repo.findFinding(id);
    if (!f) throw new AppError(ErrorCode.NotFound, "Finding not found", { safeMessage: "Item da revisão não encontrado." });
    return f;
  }

  // ═══════════════════════════════════════════════ painel, pendências, histórico

  async overview(context: RequestContext): Promise<AIReviewOverview> {
    this.auth.require(context, PermissionCode.AiReviewRead);
    const reviews = await this.repo.listReviews({ ...(context.activeClientId ? { clientId: String(context.activeClientId) } : {}), limit: 500 });
    const latest = latestPerReport(reviews);
    let lang = 0, tech = 0, crit = 0, unresolved = 0;
    for (const r of latest) {
      const findings = await this.repo.listFindings(r.id);
      lang += r.autoAppliedCount + findings.filter(f => f.severity === "LINGUISTICO" && (f.status === "APPLIED")).length;
      tech += findings.filter(f => f.severity === "TECNICO" && (f.status === "OPEN" || f.status === "ACKNOWLEDGED")).length;
      crit += findings.filter(f => f.severity === "CRITICO" && (f.status === "OPEN" || f.status === "ACKNOWLEDGED")).length;
      unresolved += findings.filter(f => f.status === "OPEN" || f.status === "ACKNOWLEDGED").length;
    }
    const settings = await this.orchestrator.settings();
    const available = settings.provider === "INSPECT_IA_LOCAL" ? await this.localProvider.isAvailable() : false;
    return {
      awaitingReview: latest.filter(r => r.status === "AI_REVIEW_REQUIRES_ACTION" || r.status === "AI_REVIEW_RUNNING" || r.status === "AI_REVIEW_FAILED").length,
      reviewed: latest.filter(r => r.status === "AI_REVIEW_PASSED" || r.status === "AI_REVIEW_WARNING").length,
      languageCorrections: lang, technicalAlerts: tech, criticalInconsistencies: crit, unresolved,
      provider: { id: settings.provider, label: AI_PROVIDER_LABELS[settings.provider], available }
    };
  }

  async listPending(context: RequestContext): Promise<AIPendingItem[]> {
    this.auth.require(context, PermissionCode.AiReviewRead);
    const reviews = latestPerReport(await this.repo.listReviews({ ...(context.activeClientId ? { clientId: String(context.activeClientId) } : {}), limit: 500 }));
    const out: AIPendingItem[] = [];
    for (const r of reviews) for (const f of await this.repo.listFindings(r.id)) if (f.status === "OPEN" || f.status === "ACKNOWLEDGED") out.push({ review: r, finding: f });
    const rank = { CRITICO: 0, TECNICO: 1, TERMINOLOGICO: 2, LINGUISTICO: 3, INFO: 4 } as const;
    return out.sort((a, b) => rank[a.finding.severity] - rank[b.finding.severity] || b.review.startedAt.localeCompare(a.review.startedAt));
  }

  async listHistory(context: RequestContext, filter: { subjectType?: AIReviewSubjectType; reportId?: string } = {}): Promise<AIReportReview[]> {
    this.auth.require(context, PermissionCode.AiReviewHistory);
    return this.repo.listReviews({ ...(context.activeClientId ? { clientId: String(context.activeClientId) } : {}), ...(filter.subjectType ? { subjectType: this.type(filter.subjectType) } : {}), ...(filter.reportId ? { reportId: filter.reportId } : {}), limit: 300 });
  }

  async listReviewFindings(context: RequestContext, reviewId: string): Promise<AIReviewFinding[]> {
    this.auth.require(context, PermissionCode.AiReviewHistory);
    const review = await this.repo.findReview(reviewId);
    if (!review) return [];
    this.requireModuleRead(context, review.subjectType);
    return this.repo.listFindings(reviewId);
  }

  async getHistoryEntry(context: RequestContext, reviewId: string): Promise<{ review: AIReportReview; findings: AIReviewFinding[]; changes: AIAppliedChange[] } | null> {
    this.auth.require(context, PermissionCode.AiReviewHistory);
    const review = await this.repo.findReview(reviewId);
    if (!review) return null;
    this.requireModuleRead(context, review.subjectType);
    const findings = await this.repo.listFindings(reviewId);
    const allChanges = await this.repo.listChanges({ subjectType: review.subjectType, reportId: review.reportId });
    const changes = allChanges.filter(c => c.reviewId === reviewId || !c.reviewId);
    return { review, findings, changes };
  }

  // ═══════════════════════════════════════════════ assistente técnico

  /**
   * ASSISTENTE TÉCNICO — contexto do relatório montado automaticamente,
   * Base Técnica com hierarquia de fontes. Nunca escreve no relatório: o
   * texto proposto só entra com "APLICAR AO CAMPO" (applyAssistantText).
   */
  async askAssistant(context: RequestContext, input: { question: string; subjectType?: AIReviewSubjectType | null; reportId?: string | null }): Promise<AIChatAnswer & { promptVersion: string; provider: string }> {
    this.auth.require(context, PermissionCode.AiAssistantUse);
    const question = (input.question || "").trim();
    if (!question) throw new AppError(ErrorCode.Validation, "Question required", { safeMessage: "Escreva a pergunta." });
    if (question.length > 2000) throw new AppError(ErrorCode.Validation, "Question too long", { safeMessage: "Pergunta muito longa (máximo 2.000 caracteres)." });
    const settings = await this.orchestrator.settings();
    if (settings.provider !== "INSPECT_IA_LOCAL") {
      throw new AppError(ErrorCode.Conflict, "Provider unavailable", { safeMessage: "Serviço INSPECT IA REVIEW temporariamente indisponível: o provedor configurado não está integrado." });
    }
    let subject = null;
    if (input.subjectType && input.reportId) {
      const t = this.type(input.subjectType);
      this.requireModuleRead(context, t);
      subject = await this.orchestrator.loadSubject(context, t, input.reportId);
    }
    const knowledge = settings.knowledgeBaseEnabled
      ? searchKnowledge(await this.repo.listKnowledge(subject?.clientId ?? (context.activeClientId ? String(context.activeClientId) : null)), `${question} ${subject ? AI_REVIEW_SUBJECT_LABELS[subject.type] : ""}`, {
          clientId: subject?.clientId ?? (context.activeClientId ? String(context.activeClientId) : null), projectId: subject?.projectId ?? null,
          procedureCodes: subject?.procedureCodes ?? [], normCodes: subject?.normCodes ?? []
        })
      : [];
    const answer = await this.localProvider.chat({
      question, subjectLabel: subject ? `${AI_REVIEW_SUBJECT_LABELS[subject.type]} ${subject.reportNumber}` : null,
      fields: subject?.fields ?? [], facts: subject?.facts ?? null, modulePendencies: subject?.modulePendencies ?? [], knowledge,
      promptVersion: promptVersionOf("TECHNICAL_ASSISTANT")
    });
    // Resposta normativa sem fonte não sai como se tivesse.
    const safe = answer.grounded ? answer : { ...answer, text: NO_KNOWLEDGE_SUPPORT, sourceRefs: [] };
    return { ...safe, promptVersion: promptVersionOf("TECHNICAL_ASSISTANT"), provider: this.localProvider.id };
  }

  /** "APLICAR AO CAMPO" — só depois da confirmação da pessoa; grava antes/depois e exige nova revisão. */
  async applyAssistantText(context: RequestContext, type: AIReviewSubjectType, reportId: string, fieldPath: string, expectedCurrentText: string, text: string): Promise<void> {
    const t = this.type(type);
    this.requireWrite(context, t, PermissionCode.AiAssistantUse);
    const subject = await this.orchestrator.loadSubject(context, t, reportId);
    if (!subject.editable) throw new AppError(ErrorCode.Conflict, "Document closed", { safeMessage: "Documento fechado para edição." });
    const field = subject.fields.find(f => f.fieldPath === fieldPath);
    if (!field || field.writable === false) throw new AppError(ErrorCode.Validation, "Field not writable", { safeMessage: "Este campo não recebe texto pela revisão." });
    if (field.text !== expectedCurrentText) throw new AppError(ErrorCode.Conflict, "Field changed", { safeMessage: "O campo mudou desde a sugestão. Peça a sugestão de novo." });
    await this.orchestrator.adapterFor(t).writeField(context, reportId, fieldPath, text);
    await this.repo.appendChange({ id: `AIC-${uuid()}`, subjectType: t, reportId, fieldPath, findingId: null, reviewId: null, kind: "ASSISTANT", before: field.text, after: text, userId: String(context.userId || "system"), at: new Date().toISOString() });
    await this.audit?.record(context, { action: AuditAction.AiCorrectionApplied, entityType: "AIReportReview", entityId: reportId, previousState: null, newState: null, metadata: { subjectType: t, fieldPath, kind: "ASSISTANT" } as never });
  }

  // ═══════════════════════════════════════════════ base técnica

  async listKnowledge(context: RequestContext): Promise<KnowledgeEntry[]> {
    if (!this.auth.can(context, PermissionCode.AiAssistantUse)) this.auth.require(context, PermissionCode.AiSettingsRead);
    return this.repo.listKnowledge(context.activeClientId ? String(context.activeClientId) : null);
  }

  async saveKnowledgeEntry(context: RequestContext, input: Partial<KnowledgeEntry> & { scope?: "GLOBAL" | "CLIENT" }): Promise<KnowledgeEntry> {
    this.auth.require(context, PermissionCode.AiSettingsUpdate);
    const errors = validateKnowledgeEntry(input);
    if (errors.length) throw new AppError(ErrorCode.Validation, errors.join(" "), { safeMessage: errors.join(" ") });
    const now = new Date().toISOString();
    const userId = String(context.userId || "system");
    const existing = input.id ? (await this.repo.listKnowledge(null)).concat(context.activeClientId ? await this.repo.listKnowledge(String(context.activeClientId)) : []).find(k => k.id === input.id) : undefined;
    const clientId = input.scope === "GLOBAL" ? null : (context.activeClientId ? String(context.activeClientId) : null);
    const entry: KnowledgeEntry = {
      id: existing?.id ?? `KB-${uuid()}`, clientId: existing ? existing.clientId : clientId, projectId: input.projectId ?? null,
      sourceType: input.sourceType!, documentCode: input.documentCode!.trim(), documentTitle: (input.documentTitle || "").trim(), revision: input.revision!.trim(),
      section: input.section!.trim(), text: input.text!.trim(), tags: (input.tags ?? []).map(t => String(t).trim()).filter(Boolean),
      status: "ACTIVE", createdAt: existing?.createdAt ?? now, createdBy: existing?.createdBy ?? userId, updatedAt: now, updatedBy: userId
    };
    await this.repo.saveKnowledge(entry);
    return entry;
  }

  async inactivateKnowledgeEntry(context: RequestContext, id: string): Promise<void> {
    this.auth.require(context, PermissionCode.AiSettingsUpdate);
    const all = [...(await this.repo.listKnowledge(null)), ...(context.activeClientId ? await this.repo.listKnowledge(String(context.activeClientId)) : [])];
    const e = all.find(k => k.id === id);
    if (!e) throw new AppError(ErrorCode.NotFound, "Entry not found", { safeMessage: "Trecho não encontrado." });
    await this.repo.saveKnowledge({ ...e, status: "INACTIVE", updatedAt: new Date().toISOString(), updatedBy: String(context.userId || "system") });
  }

  // ═══════════════════════════════════════════════ configurações

  async getSettings(context: RequestContext): Promise<AISettings & { providerLabels: Record<string, string> }> {
    this.auth.require(context, PermissionCode.AiSettingsRead);
    return { ...(await this.orchestrator.settings()), providerLabels: { ...AI_PROVIDER_LABELS } };
  }

  async updateSettings(context: RequestContext, input: Partial<AISettings>, expectedVersion: number): Promise<AISettings> {
    this.auth.require(context, PermissionCode.AiSettingsUpdate);
    try { assertNoSecrets(input as Record<string, unknown>); } catch (e) { throw new AppError(ErrorCode.Validation, "Secrets not accepted", { safeMessage: (e as Error).message }); }
    const current = await this.orchestrator.settings();
    const next = normalizeAISettings({ ...current, ...input, version: current.version + 1, updatedAt: new Date().toISOString(), updatedBy: String(context.userId || "system") });
    const ok = await this.repo.saveSettingsIfVersion(next, expectedVersion);
    if (!ok) throw new AppError(ErrorCode.Conflict, "Settings changed", { safeMessage: "As configurações foram alteradas por outra pessoa. Recarregue antes de salvar." });
    await this.audit?.record(context, {
      action: AuditAction.AiSettingsUpdated, entityType: "AISettings", entityId: "GLOBAL",
      previousState: { ...current } as never, newState: { ...next } as never, metadata: { version: next.version } as never
    });
    return next;
  }
}

function latestPerReport(reviews: readonly AIReportReview[]): AIReportReview[] {
  const seen = new Map<string, AIReportReview>();
  for (const r of reviews) {
    const key = `${r.subjectType}|${r.reportId}`;
    const cur = seen.get(key);
    if (!cur || r.startedAt > cur.startedAt) seen.set(key, r);
  }
  return [...seen.values()];
}
